import { TZDateMini } from '@date-fns/tz';
import { isSameDay, isValid } from 'date-fns';
import type { FormatDateOptions } from 'react-intl';

import { formatDate, parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import { getTimezone } from '@dynatrace-sdk/user-preferences';

import type { Precision } from './shared.types.js';
import type { CalendarType } from '../../forms/calendar/types/calendarConfiguration.js';
import type { DateRangeSelectorValueType } from '../../forms/date-time-range-picker/types.js';
import type {
  _TimeOffset,
  _TimeOffsetValue,
  Timeframe,
  TimeValue,
} from '../types/time.js';

/**
 * Compares two given timevalue objects with each other
 */
export function isEqualTimeValue(
  tv1: TimeValue | string | null,
  tv2: TimeValue | string | null,
) {
  // ref. equality check, same ref => same value
  if (tv1 === tv2) {
    return true;
  }

  if (typeof tv1 === 'string' || typeof tv2 === 'string') {
    return false;
  }

  // if only one of the objects is null, they can't be the same
  if (tv1 === null || tv2 === null) {
    return false;
  }

  // if the type differs, they must be different
  if (tv1?.type !== tv2?.type) {
    return false;
  }

  // compare by expression or iso value
  return tv1?.value === tv2?.value;
}

/**
 * Compares two given RangeSelection objects with each other
 */
export function isEqualRangeSelection(
  tf1: DateRangeSelectorValueType,
  tf2: DateRangeSelectorValueType,
) {
  // ref. equality check
  if (tf1 === tf2) {
    return true;
  }

  return (
    isEqualTimeValue(tf1?.from ?? null, tf2?.from ?? null) &&
    isEqualTimeValue(tf1?.to ?? null, tf2?.to ?? null)
  );
}
/**
 * Typeguard to see if time offset is simple, so not an object.
 * @internal
 */
export function isSimpleTimeOffset(
  offset: _TimeOffset,
): offset is _TimeOffsetValue {
  return typeof offset !== 'object';
}

/**
 * Typeguard function that will check if the date is a date range.
 * @internal
 */
export function isTimeframe(
  date: unknown,
  type: CalendarType,
): date is Partial<Timeframe> | Timeframe {
  return type === 'range';
}

/**
 * Typeguard function that will check if the date is TimeValue, it has absoluteDate, value and type.
 * @internal
 */
export function isTimeValue(value: unknown): value is TimeValue {
  return (
    typeof value === 'object' &&
    value !== null &&
    'absoluteDate' in value &&
    'value' in value &&
    'type' in value
  );
}

/**
 * Typeguard to check if a given timeframe is complete, meaning it has both `from` and `to` properties.
 * @internal
 */
export function isFullTimeframe(timeframe?: unknown): timeframe is Timeframe {
  if (typeof timeframe !== 'object' || timeframe === null) {
    return false;
  }

  const candidate = timeframe as { from?: unknown; to?: unknown };

  return (
    isTimeValue(candidate.from) &&
    candidate.from.absoluteDate !== '' &&
    isTimeValue(candidate.to) &&
    candidate.to.absoluteDate !== ''
  );
}

export function isNowTimeValue(value?: TimeValue | null): boolean {
  return Boolean(
    value?.type === 'expression' &&
      (value.value === 'now' || value.value === 'now()'),
  );
}

export function isRangeValue(
  value: TimeValue | { from: TimeValue | null; to: TimeValue | null } | null,
): value is { from: TimeValue | null; to: TimeValue | null } {
  return typeof value === 'object' && value !== null && 'from' in value;
}

/**
 * Typeguard to check if a given object is a partial timeframe. So either from or to are missing.
 * @internal
 */
export function isPartialTimeframe(
  timeframe?: unknown,
): timeframe is Partial<Timeframe> {
  if (typeof timeframe !== 'object' || timeframe === null) {
    return false;
  }

  const candidate = timeframe as { from?: unknown; to?: unknown };
  const hasFrom = 'from' in candidate && candidate.from !== null;
  const hasTo = 'to' in candidate && candidate.to !== null;

  return hasFrom !== hasTo;
}

/**
 * Formatter that gets the formatted time value strings.
 * @internal
 */
export function getFormattedTimeValue(
  timeValue: TimeValue | null,
  precision?: Precision,
  showTimeOnly?: boolean,
) {
  if (!timeValue) {
    return '';
  }

  const timezone = getTimezone();
  const formatOptions: FormatDateOptions = {
    // Extract the user-settings timezone to use for the calculation
    timeZone: getTimezone(),
    day: showTimeOnly ? undefined : 'numeric',
    month: showTimeOnly ? undefined : 'short',
    year: showTimeOnly ? undefined : 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: precision === 'minutes' ? undefined : 'numeric',
    fractionalSecondDigits: precision === 'milliseconds' ? 3 : undefined,
  };
  const absoluteDate = timeValue.absoluteDate
    ? new TZDateMini(timeValue.absoluteDate, timezone)
    : null;

  return absoluteDate && isValid(absoluteDate)
    ? formatDate(absoluteDate, formatOptions)
    : '';
}

/**
 * Formats an expression-type {@link TimeValue} as a compact label for hint/error
 * messages, e.g. `now()-7d` → `-7d`, and an exact `now()`/`now` expression as `"Now"`.
 */
function formatExpressionValue(tv: TimeValue): string {
  return isNowTimeValue(tv)
    ? '"Now"'
    : tv.value.replaceAll('now()', '').replaceAll('now', '').trim() || tv.value;
}

/**
 * Normalizes a min/max limit to a {@link TimeValue}, parsing string input against
 * a shared `relativeNow` so multiple limits parsed together stay temporally consistent.
 */
function toTimeValue(
  limit: string | TimeValue | null | undefined,
  relativeNow: number,
) {
  if (typeof limit !== 'string') {
    return limit ?? null;
  }
  return limit ? parseTimeAsTimeValue(limit, relativeNow) : null;
}

/**
 * Gets the formatted min and max values that are used in the translated messages.
 * @internal
 */
export function getFormattedMinMax(
  min?: TimeValue | null,
  max?: TimeValue | null,
  precision?: Precision,
) {
  // All parse times here need to use the same _now_ date, to not be subject
  // to millisecond differences during parsing.
  const relativeNow = TZDateMini.tz(getTimezone()).getTime();
  const minDate = toTimeValue(min, relativeNow);
  const maxDate = toTimeValue(max, relativeNow);

  const newMinDate = new TZDateMini(minDate?.absoluteDate ?? '', getTimezone());
  const isMinDateValid = !Number.isNaN(newMinDate.getTime());
  // Round up min date depending on the precision (skipped for expression types).
  if (minDate?.type !== 'expression' && isMinDateValid) {
    if (precision === 'seconds' && newMinDate.getMilliseconds() > 0) {
      newMinDate.setSeconds(newMinDate.getSeconds() + 1);
    } else if (
      precision === 'minutes' &&
      (newMinDate.getMilliseconds() > 0 || newMinDate.getSeconds() > 0)
    ) {
      newMinDate.setMinutes(newMinDate.getMinutes() + 1);
    }
  }

  const roundedMinIso = isMinDateValid
    ? newMinDate.toISOString()
    : minDate?.absoluteDate;
  const minTimeValue =
    minDate && minDate.type !== 'expression'
      ? { ...minDate, absoluteDate: roundedMinIso ?? minDate.absoluteDate }
      : null;

  const formattedMin =
    minDate?.type === 'expression'
      ? formatExpressionValue(minDate)
      : getFormattedTimeValue(minTimeValue, precision);
  const formattedMax =
    maxDate?.type === 'expression'
      ? formatExpressionValue(maxDate)
      : getFormattedTimeValue(
          maxDate,
          precision,
          !!minTimeValue &&
            !!maxDate &&
            isSameDay(minTimeValue.absoluteDate, maxDate.absoluteDate),
        );

  return {
    min: formattedMin,
    max: formattedMax,
  };
}

/**
 * Merges a partial range value into the current state value.
 * - Only keys present in `patch` are updated; absent keys keep their current value.
 * - A `null` value in `patch` explicitly clears that key.
 * - When the current state is `null`, the base defaults to `{ from: null, to: null }`.
 * @internal
 */
export function mergeRangeValue(
  current: null | { from: TimeValue | null; to: TimeValue | null },
  patch: Partial<{ from: TimeValue | null; to: TimeValue | null }>,
): { from: TimeValue | null; to: TimeValue | null } {
  const base = current ?? { from: null, to: null };
  return {
    from: 'from' in patch ? patch.from ?? null : base.from,
    to: 'to' in patch ? patch.to ?? null : base.to,
  };
}
